import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { SmartInsightDay } from 'lumen/models';
import {
  CHANNEL_NAMES_MAPPING,
  SMART_INSIGHT_DAY_ENTRY_TYPE,
  type SmartInsightEntryType,
} from 'lumen/models/smart-insights/smart-insight-day-insight-entry';

const TEXT_VARIABLE_CHANNEL_NAME = 'channel_name';
const TEXT_VARIABLE_DIFF = 'diff';

export default class SmartInsightDayRecommendationEntryModel extends Model {
  [Type] = 'smart-insights/smart-insight-day-recommendation-entry' as const;

  @belongsTo('smart-insights/smart-insight-day', {
    async: false,
    inverse: 'recommendationEntries',
  })
  declare smartInsightDay: SmartInsightDay;

  @attr() declare config: { currency: SupportedCurrencies };
  @attr() declare info: {
    recommendation_element_type?: SmartInsightEntryType;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    };
  };

  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @cached
  get formattedText(): string {
    const { template } = this.info.text;
    const processedVariables = this.processVariables();

    try {
      return this.intl.formatMessage(template, processedVariables);
    } catch (e) {
      this.errorTracking.captureMessage(
        'Smart Insights: recommendation template formatting failed',
        {
          templateKey: this.info.text.template_key,
          recommendationElementType: this.info.recommendation_element_type,
          template,
          processedVariables,
          error: e instanceof Error ? e.message : String(e),
        },
      );
      return template;
    }
  }

  private processVariableCurrency(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      const formatNumberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...(this.config.currency && {
          style: 'currency' as const,
          currency: this.config.currency,
          currencyDisplay: 'symbol' as const,
        }),
      };

      processedVariables[key] = this.intl.formatNumber(
        valueAsNumber,
        formatNumberOptions,
      );
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableCurrencyWithSign(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      const formatNumberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'always' as const,
        ...(this.config.currency && {
          style: 'currency' as const,
          currency: this.config.currency,
          currencyDisplay: 'symbol' as const,
        }),
      };

      processedVariables[key] = this.intl.formatNumber(
        valueAsNumber,
        formatNumberOptions,
      );
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableNumber(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      processedVariables[key] = this.intl.formatNumber(valueAsNumber, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariables(): Record<string, string | number> {
    const processedVariables: Record<string, string | number> = {};

    switch (this.info.recommendation_element_type) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          if (key === TEXT_VARIABLE_DIFF) {
            this.processVariableCurrencyWithSign(
              key,
              value,
              processedVariables,
            );
          } else if (key === TEXT_VARIABLE_CHANNEL_NAME) {
            processedVariables[key] =
              CHANNEL_NAMES_MAPPING[
                value as keyof typeof CHANNEL_NAMES_MAPPING
              ] || value;
          } else {
            this.processVariableCurrency(key, value, processedVariables);
          }
        });
        break;
      case SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          this.processVariableNumber(key, value, processedVariables);
        });
        break;
      default:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          this.processVariableCurrency(key, value, processedVariables);
        });
        break;
    }

    return processedVariables;
  }
}
